import {
  JOURNEY_ROUTE,
  PURCHASE_INTENT,
  PURCHASE_INTENT_STORAGE_KEY,
  PURCHASE_INTENT_TTL_MS,
  type JourneyRoute,
  type PurchaseIntent,
} from '@/lib/constants/issue-report';

interface StoredIntent {
  intent: PurchaseIntent;
  /** Epoch milliseconds. */
  expiresAt: number;
  /** Register or log in, set by the page the visitor chose; absent when they were already signed in. */
  route?: JourneyRoute;
}

/**
 * The same record held in memory, so a browser that blocks web storage still
 * carries the intent for the page session and then falls back to the dashboard
 * (FR-206, design §39 failure modes). Not a credential store: it holds only the
 * intent name, which is why it is safe here. The pending-login credential stays
 * in `pre-auth.ts`, and nothing from that module is touched.
 *
 * It is only the source of truth while storage is unusable (`memoryOnly`).
 * Otherwise storage is, so another tab clearing the intent also clears it here.
 */
let memoryRecord: StoredIntent | null = null;
/** True when the last write to storage failed, so the memory copy stands in for it. */
let memoryOnly = false;

/** Pure: the intent a raw value names, or null for anything but a known intent. */
export function parsePurchaseIntent(value: unknown): PurchaseIntent | null {
  return value === PURCHASE_INTENT.ISSUE_REPORT ? value : null;
}

function isStoredIntent(value: unknown): value is StoredIntent {
  if (typeof value !== 'object' || value === null) return false;
  const { intent, expiresAt, route } = value as Partial<StoredIntent>;
  const routeOk =
    route === undefined || route === JOURNEY_ROUTE.REGISTER || route === JOURNEY_ROUTE.LOGIN;
  return (
    parsePurchaseIntent(intent) !== null &&
    typeof expiresAt === 'number' &&
    Number.isFinite(expiresAt) &&
    routeOk
  );
}

/** What the storage slot held: nothing, something unusable, or a record. Storage may throw. */
type StoredRead = { kind: 'none' } | { kind: 'invalid' } | { kind: 'ok'; record: StoredIntent } | { kind: 'blocked' };

function readStored(): StoredRead {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(PURCHASE_INTENT_STORAGE_KEY);
  } catch {
    // Blocked storage reads as empty; the in-memory copy covers the page session.
    return { kind: 'blocked' };
  }
  if (raw === null) return { kind: 'none' };
  try {
    const parsed: unknown = JSON.parse(raw);
    return isStoredIntent(parsed) ? { kind: 'ok', record: parsed } : { kind: 'invalid' };
  } catch {
    return { kind: 'invalid' };
  }
}

function removeStored(): void {
  try {
    window.localStorage.removeItem(PURCHASE_INTENT_STORAGE_KEY);
  } catch {
    // Blocked storage has nothing to remove.
  }
}

/** The unexpired record in force: storage while usable, else the in-memory copy. */
function currentRecord(now: number = Date.now()): StoredIntent | null {
  const stored = readStored();
  if (stored.kind === 'invalid') return null;
  const record = stored.kind === 'ok' ? stored.record : stored.kind === 'blocked' || memoryOnly ? memoryRecord : null;
  return record && record.expiresAt > now ? record : null;
}

/**
 * Remember the intent for 7 days. Never throws; a blocked storage keeps it in
 * memory only. Without a `route`, the one already stored is kept, so renewing
 * the intent later in the journey does not forget how it began.
 */
export function storePurchaseIntent(
  intent: PurchaseIntent,
  now: number = Date.now(),
  route: JourneyRoute | undefined = currentRecord()?.route,
): void {
  const record: StoredIntent = {
    intent,
    expiresAt: now + PURCHASE_INTENT_TTL_MS,
    ...(route && { route }),
  };
  memoryRecord = record;
  try {
    window.localStorage.setItem(PURCHASE_INTENT_STORAGE_KEY, JSON.stringify(record));
    memoryOnly = false;
  } catch {
    // Quota, private mode or blocked storage: the in-memory copy carries it.
    memoryOnly = true;
  }
}

/**
 * The stored intent, or null, without touching storage: safe to call while
 * rendering. An expired or unusable record reads as no intent but is left for
 * `readPurchaseIntent` or the next store to remove. The memory copy answers only
 * while storage is unusable, so another tab clearing the intent is honoured.
 */
export function peekPurchaseIntent(now: number = Date.now()): PurchaseIntent | null {
  return currentRecord(now)?.intent ?? null;
}

/**
 * How the stored journey began, for its progress bar (FR-224). A visitor who
 * was already signed in has none and counts as `login`. Safe while rendering.
 */
export function peekJourneyRoute(): JourneyRoute {
  return currentRecord()?.route ?? JOURNEY_ROUTE.LOGIN;
}

/**
 * The stored intent, or null, removing an expired record, a value other than
 * `issue-report` and unparseable text (FR-206). It writes, so call it from an
 * effect or an event handler; use `peekPurchaseIntent` while rendering.
 */
export function readPurchaseIntent(now: number = Date.now()): PurchaseIntent | null {
  const intent = peekPurchaseIntent(now);
  if (intent === null) clearPurchaseIntent();
  return intent;
}

/**
 * Forget the intent: on reaching checkout, on the journey's back or cancel
 * link, and on expiry (FR-206). Never throws.
 */
export function clearPurchaseIntent(): void {
  memoryRecord = null;
  memoryOnly = false;
  removeStored();
}
